import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { FaStop, FaVolumeUp } from 'react-icons/fa';
import Styles from './VideoResources.module.scss';
import {
    extractYoutubeId,
    loadPodcastEpisodes,
    normalizeEpisodeImageUrl,
} from '../../service/podcastService';
import { urls } from '../../utils/urls';

const SITE_ORIGIN = 'https://iamironlady.com';

/** Plain text for meta tags (podcast summaries may use **bold** markers). */
function stripPodcastMarkdownForMeta(text) {
    if (!text || typeof text !== 'string') return '';
    return text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\s+/g, ' ').trim();
}

function clampMetaDescription(text, max = 158) {
    const plain = stripPodcastMarkdownForMeta(text);
    if (!plain) return '';
    if (plain.length <= max) return plain;
    return `${plain.slice(0, max - 1).trim()}…`;
}

const CMS_NOTE_DISMISS = 'il_podcast_cms_note_dismissed';

function truncateSummary(text, max = 120) {
    if (!text || typeof text !== 'string') return '';
    const t = text.trim();
    if (t.length <= max) return t;
    return `${t.slice(0, max).trim()}…`;
}

/**
 * Podcast summaries from the CMS are plain text (`podcast_summary`). Wrap a phrase in
 * **double asterisks** for bold in the app.
 */
function PodcastSummaryWithBold({ text }) {
    if (!text || typeof text !== 'string') return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
        const inner = part.match(/^\*\*([^*]+)\*\*$/);
        if (inner) {
            return <strong key={`b-${String(i)}`}>{inner[1]}</strong>;
        }
        return <React.Fragment key={`t-${String(i)}`}>{part}</React.Fragment>;
    });
}

/** Supports embed URLs with optional query string (e.g. ?si=). */
function embedUrlToYoutubeId(url) {
    if (!url) return '';
    const afterEmbed = url.split('/embed/')[1];
    if (!afterEmbed) return '';
    return afterEmbed.split(/[?&#]/)[0] || '';
}

/** Standalone Iron Lady Speaks: list vs episode detail in URL */
function speaksEpisodeUrl(episodeId) {
    return `/resources/video-resources?speaksOnly=1&episode=${episodeId}`;
}

/** US-style date for episode cards (e.g. 4/13/2026); empty if missing/invalid */
function formatEpisodeDate(iso) {
    if (!iso || typeof iso !== 'string') return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    try {
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
    } catch {
        return '';
    }
}

const SPEAKS_PAGE_SIZE = 12;

const videoData = {
    recommended: [
        { id: 101, title: 'Rebuilding Strength, Redefining Success - Shilpa K', url: 'https://www.youtube.com/embed/qLj2PYqSsWs' },
        { id: 102, title: 'Lakshmi Nayak’s 10x Transformation with Iron Lady', url: 'https://www.youtube.com/embed/125sdAgVEjo' },
        { id: 103, title: 'Unstoppable Growth: How Iron Lady Transformed My Life and Career', url: 'https://www.youtube.com/embed/wSr0iEKLkGs' },
        { id: 104, title: 'Master the Art of Negotiation with Iron Lady!', url: 'https://www.youtube.com/embed/h4jMg9cXsUQ' },
        { id: 105, title: 'Experience Seema Rajashekar remarkable journey of transformation and growth with Iron Lady', url: 'https://www.youtube.com/embed/cJYp9DHY7Vs' },
        { id: 106, title: 'Experience Sharmila Gorde\'s incredible journey of transformation and growth with Iron Lady.', url: 'https://www.youtube.com/embed/lkF67OuJfW8' }
    ],
    testimonials: [
        { id: 201, title: 'Life took a 360-degree turn after the LEP program – Dr. Reshma S', url: 'https://www.youtube.com/embed/mKm2fZIZEwU' },
        { id: 202, title: "Transformative Leadership: Lt Col Amarjeet's Journey with Iron Lady", url: 'https://www.youtube.com/embed/3gKYCOArGVU' },
        { id: 203, title: "Transformative Leadership: Anghrija's Journey with Iron Lady", url: 'https://www.youtube.com/embed/2nhdlg40Mvk' },
        { id: 204, title: "Meet Tanvi Hemant Jadhav, a shining example of Iron Lady's transformative power!", url: 'https://www.youtube.com/embed/Q0eMCzRiZKI' },
        { id: 205, title: "Meet Renimol Robert, a shining example of what’s possible with Iron Lady! 🚀", url: 'https://www.youtube.com/embed/ccZqwuGA9a0' },
        { id: 206, title: 'Breaking Through Limits: From Confidence Lost to Leadership Gained!', url: 'https://www.youtube.com/embed/P98TafL77bE' },
        { id: 207, title: 'I don’t even know how many times my salary has grown since I started the program!', url: 'https://www.youtube.com/embed/3NXgm-WEg6c' },
        { id: 208, title: 'From IT Pro to Leadership Coach - Tiju Iype', url: 'https://www.youtube.com/embed/HiW2fCH9kbU' },
        { id: 209, title: 'From Consultant to Global Strategy Expert!', url: 'https://www.youtube.com/embed/BC9n4Qp7Bq0' },
        { id: 210, title: 'From Discovery to Empowerment – Deepika’s Story', url: 'https://www.youtube.com/embed/QYOvKpzMYOs' },
        { id: 211, title: 'From Resignation to Boardrooms!', url: 'https://www.youtube.com/embed/bQg7uSnGAiU' },
        { id: 212, title: 'From Uncertainty to Unstoppable Confidence – Journey', url: 'https://www.youtube.com/embed/mBibyBlCcFw' },
        { id: 213, title: "Ashwini Cracks the Code with Iron Lady's Shameless Pitching", url: 'https://www.youtube.com/embed/y_JyS9_sRdg' }
    ],
    csuiteleague: [
        { id: 301, title: 'Why Women Lose Out in Financial Planning ', url: 'https://www.youtube.com/embed/rlbPjItASzo' },
        { id: 302, title: 'No More “Happy at Work” Sessions ', url: 'https://www.youtube.com/embed/lMVbvGOW_K8' },
        { id: 303, title: 'HR as a 10X Growth Engine: 3 Transformational Strategies by Rekha Nagraj ', url: 'https://www.youtube.com/embed/2A3gV1I84XI' },
        { id: 304, title: 'This One Tiny Mistake Crashed a Million-Dollar System ', url: 'https://www.youtube.com/embed/2-IOYU5Zvto' },
        { id: 305, title: 'What if your maintenance strategy could boost EBITDA in just 90 days?', url: 'https://www.youtube.com/embed/o4pHxleizBI' },
        { id: 306, title: 'More Than 70% Of Products Fail 🙁 How Do You Build Products That Scale?', url: 'https://www.youtube.com/embed/ZG5peaEUfho' },
        { id: 307, title: 'Why Pharma Marketing Is Failing – And How to Win!', url: 'https://www.youtube.com/embed/2jD_3tsbdfo' },
        { id: 308, title: 'Netflix Didnot Trust What You Said. It Trusted What You Did | C-Suite Speaks - Divya', url: 'https://www.youtube.com/embed/Sx0z7qB8HDI' },
        { id: 309, title: 'HR Is Considered as Rangoli Maker at Your Workplace?', url: 'https://www.youtube.com/embed/XScewfOSzaQ' },
        { id: 310, title: 'Mastering Sales Excellence: Sunita Shrestha Strategy For Market Domination', url: 'https://www.youtube.com/embed/oVwPypcrwrQ' },
        { id: 311, title: 'What made Netflix thrive while blockbuster failed?', url: 'https://www.youtube.com/embed/WxRMU-xd8qc' },
    ],

    csuiteconservations: [
        { id: 401, title: 'They Told Me to Focus on the Baby… I Chose to Lead ', url: 'https://www.youtube.com/embed/sfHZriNVsMU' },
        { id: 402, title: 'HR is Not Just About People. Its About Business - Nithya Chandar', url: 'https://www.youtube.com/embed/7ZS7mw_4lC8' },
        { id: 403, title: 'Gunjan Singh’s Bold Move to the ₹1 CR Club ', url: 'https://www.youtube.com/embed/GCqjoQOLDlU' },
        { id: 404, title: 'From Its not for a women to Its My ₹5 CR Business ', url: 'https://www.youtube.com/embed/t_BCDiMWubs' },
        { id: 405, title: 'They Doubted Me, But I Became a Tech Leader ', url: 'https://www.youtube.com/embed/zbl5cQUonCM' },
        { id: 406, title: 'Software Engineer to $4 Million in Revenue Entrepreneur in 2 Year ', url: 'https://www.youtube.com/embed/KWJx0wQAq4E' },
        { id: 407, title: 'One Decision That Changed My life ', url: 'https://www.youtube.com/embed/PfLLQzB90Jw' },
        { id: 408, title: 'How I Built a Powerful Legal Career', url: 'https://www.youtube.com/embed/GOyWuM1dM-g' },
        { id: 409, title: 'HE said I cant handle tough jobs because I am soft-spoken Woman', url: 'https://www.youtube.com/embed/lvHcGgv-cvI' },
    ],

    // Fallback when WordPress REST has no playable URLs in meta/content. Order: newest first when CMS works.
    podcast: [
        {
            id: 501,
            title: 'new podcast coming soon',
            url: 'https://www.youtube.com/embed/85wKIwhd7Qg',
        },
        {
            id: 502,
            title: 'new podcast 2',
            url: 'https://www.youtube.com/embed/lvHcGgv-cvI',
        },
    ],
};

/** Tabs on the Video Resources page only (Iron Lady Speaks is reachable via Resources nav → speaksOnly). */
const RESOURCE_PAGE_TAB_IDS = Object.keys(videoData).filter((k) => k !== 'podcast');

function readInitialTabFromUrl() {
    if (typeof window === 'undefined') return 'recommended';
    const params = new URLSearchParams(window.location.search);
    if (params.get('speaksOnly') === '1') return 'podcast';
    const t = params.get('tab');
    /* ?tab=podcast without speaksOnly is migrated in layout effect */
    if (t === 'podcast') return 'recommended';
    return RESOURCE_PAGE_TAB_IDS.includes(t) ? t : 'recommended';
}

function listForTab(tab, cmsPodcasts) {
    if (tab === 'podcast') {
        return cmsPodcasts && cmsPodcasts.length > 0 ? cmsPodcasts : videoData.podcast;
    }
    return videoData[tab];
}

const tabNames = {
    recommended: 'Recommended',
    testimonial: 'Testimonial',
    csuiteleague: 'C Suite Speaks',
    csuiteconservations: 'C Suite Conversations',
    podcast: 'Iron Lady Speaks',
};
const relatedSubheading = {
    recommended: 'Recommended',
    testimonial: 'Testimonial',
    csuiteleague: 'C Suite Speaks ',
    csuiteconservations: 'C Suite Conversations',
    podcast: 'Iron Lady Speaks',
};

const VideoResources = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(readInitialTabFromUrl);
    const [videos, setVideos] = useState(() => listForTab(readInitialTabFromUrl(), null));
    const [currentVideo, setCurrentVideo] = useState(() => {
        const list = listForTab(readInitialTabFromUrl(), null);
        return list[0] || videoData.recommended[0];
    });
    const iframeRef = useRef(null);
    const isInitialRender = useRef(true);
    const [visibleVideos, setVisibleVideos] = useState(3);
    const [speaksQuery, setSpeaksQuery] = useState('');
    const [speaksPageNum, setSpeaksPageNum] = useState(1);
    /** CMS podcast list: null = not loaded; if loaded and non-empty, use CMS; otherwise fall back to static podcast list */
    const [cmsPodcasts, setCmsPodcasts] = useState(null);
    const [podcastCmsInfo, setPodcastCmsInfo] = useState(null);
    const [dismissCmsNote, setDismissCmsNote] = useState(
        () => typeof sessionStorage !== 'undefined' && sessionStorage.getItem(CMS_NOTE_DISMISS) === '1',
    );
    const [isSpeakingSummary, setIsSpeakingSummary] = useState(false);
    const [summaryExpanded, setSummaryExpanded] = useState(false);
    const [summaryHasOverflow, setSummaryHasOverflow] = useState(false);
    const summaryTextRef = useRef(null);

    /** From Resources → Iron Lady Speaks only: podcasts-only layout (Resources nav uses ?speaksOnly=1) */
    const speaksOnly = searchParams.get('speaksOnly') === '1';
    const speaksEpisodeParam = searchParams.get('episode');
    const speaksListMode = speaksOnly && !speaksEpisodeParam;
    const speaksDetailMode = speaksOnly && Boolean(speaksEpisodeParam);

    /** Old links (?tab=podcast without speaksOnly) → standalone Iron Lady Speaks view */
    useLayoutEffect(() => {
        if (searchParams.get('tab') !== 'podcast' || searchParams.get('speaksOnly') === '1') return;
        setSearchParams({ tab: 'podcast', speaksOnly: '1' }, { replace: true });
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        setSummaryExpanded(false);
    }, [currentVideo?.id]);

    useLayoutEffect(() => {
        if (speaksOnly) {
            setSummaryHasOverflow(false);
            return undefined;
        }
        if (activeTab !== 'podcast' || !currentVideo?.summary) {
            setSummaryHasOverflow(false);
            return undefined;
        }
        if (summaryExpanded) {
            return undefined;
        }
        let cancelled = false;
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (cancelled) return;
                const el = summaryTextRef.current;
                if (!el) return;
                setSummaryHasOverflow(el.scrollHeight > el.clientHeight + 1);
            });
        });
        return () => {
            cancelled = true;
            cancelAnimationFrame(id);
        };
    }, [speaksOnly, activeTab, currentVideo?.id, currentVideo?.summary, summaryExpanded]);

    const handleListenSummary = useCallback(() => {
        if (typeof window === 'undefined' || !window.speechSynthesis) return;
        if (!currentVideo?.summary) return;
        if (window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
            setIsSpeakingSummary(false);
            return;
        }
        const u = new SpeechSynthesisUtterance(currentVideo.summary.replace(/\*\*([^*]+)\*\*/g, '$1'));
        u.lang = 'en-IN';
        u.onend = () => setIsSpeakingSummary(false);
        u.onerror = () => setIsSpeakingSummary(false);
        window.speechSynthesis.speak(u);
        setIsSpeakingSummary(true);
    }, [currentVideo]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        setIsSpeakingSummary(false);
    }, [currentVideo]);

    useEffect(() => {
        if (activeTab === 'podcast') return;
        if (typeof window === 'undefined' || !window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        setIsSpeakingSummary(false);
    }, [activeTab]);

    const resolvePodcastVideos = () => {
        if (cmsPodcasts && cmsPodcasts.length > 0) return cmsPodcasts;
        return videoData.podcast;
    };

    // Load podcasts when Iron Lady Speaks view is active (?speaksOnly=1 → activeTab podcast)
    useEffect(() => {
        if (activeTab !== 'podcast') return undefined;
        let cancelled = false;
        loadPodcastEpisodes()
            .then((r) => {
                if (cancelled) return;
                setCmsPodcasts(r.list);
                setPodcastCmsInfo({
                    bundleStatus: r.bundleStatus,
                    v2PostCount: r.v2PostCount,
                    v2MappedCount: r.v2MappedCount,
                });
            })
            .catch(() => {
                if (!cancelled) {
                    setCmsPodcasts(null);
                    setPodcastCmsInfo(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== 'podcast') return;
        const list =
            cmsPodcasts && cmsPodcasts.length > 0 ? cmsPodcasts : videoData.podcast;
        if (!list?.length) return;
        setVideos(list);
        const episodeParam = searchParams.get('episode');
        if (speaksOnly && episodeParam) {
            const picked = list.find((v) => String(v.id) === String(episodeParam));
            if (picked) {
                setCurrentVideo(picked);
            }
        } else {
            setCurrentVideo(list[0]);
        }
        setVisibleVideos(3);
        isInitialRender.current = true;
    }, [cmsPodcasts, activeTab, speaksOnly, searchParams]);

    useEffect(() => {
        if (iframeRef.current && currentVideo?.url) {
            const videoUrl = isInitialRender.current
                ? currentVideo.url
                : `${currentVideo.url}?autoplay=1`;
            iframeRef.current.src = videoUrl;
            isInitialRender.current = false;
        }
    }, [currentVideo]);

    const handleTabClick = (tab) => {
        if (speaksOnly) return;
        setActiveTab(tab);
        const list = videoData[tab];
        setVideos(list);
        setCurrentVideo(list[0]);
        setVisibleVideos(3);
        isInitialRender.current = true;
        setSearchParams(tab === 'recommended' ? {} : { tab });
    };

    useEffect(() => {
        if (speaksOnly) {
            if (activeTab !== 'podcast') {
                setActiveTab('podcast');
                const list = resolvePodcastVideos();
                if (list?.length) {
                    setVideos(list);
                    const episodeParam = searchParams.get('episode');
                    if (episodeParam) {
                        const picked = list.find((v) => String(v.id) === String(episodeParam));
                        if (picked) {
                            setCurrentVideo(picked);
                        }
                    } else {
                        setCurrentVideo(list[0]);
                    }
                    setVisibleVideos(3);
                    isInitialRender.current = true;
                }
            }
            return;
        }
        const t = searchParams.get('tab');
        if (!t || !RESOURCE_PAGE_TAB_IDS.includes(t)) return;
        if (t === activeTab) return;
        setActiveTab(t);
        const list = videoData[t];
        if (!list?.length) return;
        setVideos(list);
        setCurrentVideo(list[0]);
        setVisibleVideos(3);
        isInitialRender.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL tab when query updates without route remount (speaksOnly uses separate branch)
    }, [searchParams, speaksOnly]);

    /** Invalid ?episode= keeps stale UI — send user back to the episode grid */
    useEffect(() => {
        if (!speaksOnly || !speaksEpisodeParam || !videos.length) return;
        const match = videos.find((v) => String(v.id) === String(speaksEpisodeParam));
        if (!match) {
            setSearchParams({ speaksOnly: '1' }, { replace: true });
        }
    }, [speaksOnly, speaksEpisodeParam, videos, setSearchParams]);

    /** Keep player + article in sync when episode id in URL changes (incl. browser back/forward) */
    useEffect(() => {
        if (!speaksOnly || !speaksEpisodeParam || !videos.length) return;
        const match = videos.find((v) => String(v.id) === String(speaksEpisodeParam));
        if (!match) return;
        setCurrentVideo((prev) => {
            if (prev?.id === match.id) return prev;
            isInitialRender.current = true;
            return match;
        });
    }, [speaksOnly, speaksEpisodeParam, videos]);

    /** Episode detail has no search bar — avoid a stale list filter hiding episodes */
    useEffect(() => {
        if (!speaksOnly || !speaksEpisodeParam) return;
        setSpeaksQuery('');
        setSpeaksPageNum(1);
    }, [speaksOnly, speaksEpisodeParam]);

    const handleVideoClick = (video) => {
        setCurrentVideo(video);
        if (!speaksOnly) {
            const updatedVideos = videos.filter((v) => v.id !== video.id);
            setVideos([video, ...updatedVideos]);
        }
    };

    const handleLoadMore = () => {
        setVisibleVideos((prev) => Math.min(prev + 3, videos.length));
    };

    const labelBase = relatedSubheading[activeTab] || activeTab.charAt(0).toUpperCase() + activeTab.slice(1);

    const speaksFilteredVideos = useMemo(() => {
        if (!speaksOnly) return [];
        const q = speaksQuery.trim().toLowerCase();
        if (!q) return videos;
        return videos.filter((v) => v.title.toLowerCase().includes(q));
    }, [speaksOnly, videos, speaksQuery]);

    const speaksTotalPages = useMemo(
        () => Math.max(1, Math.ceil(speaksFilteredVideos.length / SPEAKS_PAGE_SIZE)),
        [speaksFilteredVideos.length],
    );

    const speaksPagedVideos = useMemo(() => {
        if (!speaksOnly) return [];
        const start = (speaksPageNum - 1) * SPEAKS_PAGE_SIZE;
        return speaksFilteredVideos.slice(start, start + SPEAKS_PAGE_SIZE);
    }, [speaksOnly, speaksFilteredVideos, speaksPageNum]);

    useEffect(() => {
        if (!speaksOnly) return;
        setSpeaksPageNum((p) => (p > speaksTotalPages ? speaksTotalPages : p));
    }, [speaksOnly, speaksTotalPages]);

    const seoCanonicalPath = useMemo(() => {
        if (speaksOnly && speaksEpisodeParam) {
            return `/resources/video-resources?speaksOnly=1&episode=${encodeURIComponent(String(speaksEpisodeParam))}`;
        }
        if (speaksOnly) {
            return '/resources/video-resources?speaksOnly=1';
        }
        const tab = searchParams.get('tab');
        if (tab && RESOURCE_PAGE_TAB_IDS.includes(tab)) {
            return `/resources/video-resources?tab=${encodeURIComponent(tab)}`;
        }
        return '/resources/video-resources';
    }, [speaksOnly, speaksEpisodeParam, searchParams]);

    const canonicalUrl = `${SITE_ORIGIN}${seoCanonicalPath}`;

    const { seoTitle, seoDescription, seoOgImage } = useMemo(() => {
        if (speaksDetailMode && currentVideo?.title) {
            const summarySnippet =
                clampMetaDescription(currentVideo.summary || '') ||
                `Watch "${currentVideo.title}" — Iron Lady Speaks leadership conversations for ambitious women leaders.`;
            const ytId = extractYoutubeId(currentVideo.url || '');
            const thumb =
                normalizeEpisodeImageUrl(currentVideo.imageUrl) ||
                (ytId ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg` : urls.ironLadyLanding);
            return {
                seoTitle: `${currentVideo.title} | Iron Lady Speaks`,
                seoDescription: summarySnippet,
                seoOgImage: thumb,
            };
        }
        if (speaksOnly) {
            return {
                seoTitle: 'Iron Lady Speaks — Leadership Podcast | Iron Lady',
                seoDescription:
                    'Iron Lady Speaks: video podcasts and leadership conversations with Iron Lady — practical insights for women leaders, curated talks, and episodes on strategy, confidence, and career growth.',
                seoOgImage: urls.ironLadyLanding,
            };
        }
        return {
            seoTitle: 'Video Resources | Iron Lady',
            seoDescription:
                'Iron Lady video resources: curated talks, testimonials, C-suite conversations, and leadership clips for women who want to grow faster.',
            seoOgImage: urls.ironLadyLanding,
        };
    }, [speaksOnly, speaksDetailMode, currentVideo]);

    const speaksVideoStructuredData = useMemo(() => {
        if (!speaksDetailMode || !currentVideo?.title || !currentVideo?.url) return null;
        const ytId = extractYoutubeId(currentVideo.url);
        const thumbnailUrl =
            normalizeEpisodeImageUrl(currentVideo.imageUrl) ||
            (ytId ? `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg` : urls.ironLadyLanding);
        const desc =
            clampMetaDescription(currentVideo.summary || currentVideo.title, 5000) ||
            clampMetaDescription(currentVideo.title, 5000);
        return {
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: currentVideo.title,
            description: desc,
            thumbnailUrl,
            embedUrl: currentVideo.url,
            url: canonicalUrl,
            ...(currentVideo.publishedAt ? { uploadDate: currentVideo.publishedAt } : {}),
            publisher: {
                '@type': 'Organization',
                name: 'Iron Lady',
                logo: {
                    '@type': 'ImageObject',
                    url: urls.blackLogo,
                },
            },
        };
    }, [speaksDetailMode, currentVideo, canonicalUrl]);

    const publicUrl = process.env.PUBLIC_URL || '';

    const showPodcastCmsNote =
        activeTab === 'podcast' &&
        !dismissCmsNote &&
        podcastCmsInfo &&
        (podcastCmsInfo.bundleStatus === 404 ||
            podcastCmsInfo.bundleStatus === 0 ||
            (podcastCmsInfo.v2PostCount > 0 && podcastCmsInfo.v2MappedCount < podcastCmsInfo.v2PostCount));

    /** Video resources tabs: player + optional summary card above fold */
    const classicPlayerInner = (
        <>
            <div className={Styles.videoFrame}>
                <iframe
                    ref={iframeRef}
                    src={currentVideo.url}
                    title={currentVideo.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            </div>
            <div className={Styles.videoMeta}>
                <h2 className={Styles.videoTitle}>{currentVideo.title}</h2>
                <a
                    href="https://www.youtube.com/@iamironlady?sub_confirmation=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={Styles.subscribeBtn}
                >
                    Subscribe
                </a>
            </div>
            {activeTab === 'podcast' && currentVideo.summary && (
                <div className={Styles.podcastSummary}>
                    <div className={Styles.podcastSummaryAccent} aria-hidden />
                    <div className={Styles.podcastSummaryContent}>
                        <div className={Styles.podcastSummaryTop}>
                            <div className={Styles.podcastSummaryLabelBlock}>
                                <span className={Styles.podcastSummaryEyebrow}>From this talk</span>
                                <h3 className={Styles.podcastSummaryLabel}>At a glance</h3>
                            </div>
                            {typeof window !== 'undefined' &&
                                typeof window.speechSynthesis !== 'undefined' && (
                                <button
                                    type="button"
                                    className={Styles.listenSummaryBtn}
                                    onClick={handleListenSummary}
                                    aria-pressed={isSpeakingSummary}
                                >
                                    {isSpeakingSummary ? (
                                        <>
                                            <span className={Styles.listenSummaryIcon} aria-hidden>
                                                <FaStop />
                                            </span>
                                            Stop
                                        </>
                                    ) : (
                                        <>
                                            <span className={Styles.listenSummaryIcon} aria-hidden>
                                                <FaVolumeUp />
                                            </span>
                                            Listen
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                        <p
                            ref={summaryTextRef}
                            className={
                                summaryExpanded
                                    ? `${Styles.podcastSummaryText} ${Styles.podcastSummaryTextExpanded}`
                                    : `${Styles.podcastSummaryText} ${Styles.podcastSummaryTextClamped}`
                            }
                        >
                            <PodcastSummaryWithBold text={currentVideo.summary} />
                        </p>
                        {(summaryHasOverflow || summaryExpanded) && (
                            <button
                                type="button"
                                className={
                                    summaryExpanded
                                        ? `${Styles.summaryToggle} ${Styles.summaryToggleExpanded}`
                                        : Styles.summaryToggle
                                }
                                onClick={() => setSummaryExpanded((e) => !e)}
                                aria-expanded={summaryExpanded}
                            >
                                {summaryExpanded ? 'Show less' : 'Expand full summary'}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </>
    );

    const speaksEpisodeDateDisplay = formatEpisodeDate(currentVideo?.publishedAt);
    const speaksArticleSummary = speaksDetailMode ? currentVideo.summary : '';
    const speaksArticleFeature =
        speaksDetailMode && currentVideo?.url ? (
        <article className={Styles.speaksArticle}>
            <header className={Styles.speaksArticleLead}>
                <div className={Styles.speaksArticleInner}>
                    <p className={Styles.speaksArticleSeries}>Iron Lady Speaks · Podcast</p>
                    <h1 className={Styles.speaksArticleHeadline}>{currentVideo.title}</h1>
                    <div
                        className={Styles.speaksArticlePostHead}
                        aria-label="Publisher and publication date"
                    >
                        <div className={Styles.speaksArticlePostHeadRow}>
                            <div className={Styles.speaksArticleBrandBadge}>
                                <img
                                    src={urls.whiteLogo}
                                    alt=""
                                    className={Styles.speaksArticleBrandBadgeImg}
                                    decoding="async"
                                    loading="lazy"
                                />
                            </div>
                            <span className={Styles.speaksArticleBrandName}>Iron Lady</span>
                            {speaksEpisodeDateDisplay && currentVideo?.publishedAt ? (
                                <>
                                    <span className={Styles.speaksArticlePostHeadSep} aria-hidden>
                                        •
                                    </span>
                                    <time
                                        className={Styles.speaksArticlePostHeadDate}
                                        dateTime={currentVideo.publishedAt}
                                    >
                                        {speaksEpisodeDateDisplay}
                                    </time>
                                </>
                            ) : null}
                            <span className={Styles.speaksArticlePostHeadSep} aria-hidden>
                                •
                            </span>
                            <a
                                href={urls.ilYoutube}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={Styles.speaksArticlePostHeadSeriesLink}
                            >
                                YouTube channel
                            </a>
                        </div>
                        <span className={Styles.speaksArticlePostHeadRule} aria-hidden />
                    </div>
                </div>
            </header>

            <div
                id="il-speaks-player"
                className={`${Styles.speaksArticleHero} ${Styles.speaksArticleHeroVideo}`}
            >
                <div className={`${Styles.videoFrame} ${Styles.speaksArticleVideoEmbed}`}>
                    <iframe
                        ref={iframeRef}
                        src={currentVideo.url}
                        title={currentVideo.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>
            </div>

            <div className={Styles.speaksArticleInner}>
                {activeTab === 'podcast' && speaksArticleSummary ? (
                    <div className={Styles.speaksArticleProseWrap}>
                        <p className={Styles.speaksArticleLeadEyebrow}>About this talk</p>
                        <div className={Styles.speaksArticleProse}>
                            <p
                                className={`${Styles.speaksArticleBody} ${Styles.speaksArticleBodyFull}`}
                            >
                                <PodcastSummaryWithBold text={speaksArticleSummary} />
                            </p>
                        </div>
                    </div>
                ) : null}
            </div>
        </article>
        ) : null;

    return (
        <div className={`${Styles.pageWrapper}${speaksOnly ? ` ${Styles.pageWrapperSpeaks}` : ''}`}>
            <Helmet>
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content={speaksDetailMode ? 'video.other' : 'website'} />
                <meta property="og:image" content={seoOgImage} />
                <meta property="og:site_name" content="Iron Lady" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={seoOgImage} />
                {speaksVideoStructuredData ? (
                    <script type="application/ld+json">{JSON.stringify(speaksVideoStructuredData)}</script>
                ) : null}
            </Helmet>
            {!speaksOnly ? (
                <section className={Styles.hero} aria-label="Video resources">
                    <div
                        className={Styles.heroBgImage}
                        style={{ backgroundImage: `url(${publicUrl}/background.png)` }}
                        aria-hidden
                    />
                    <div className={Styles.heroOverlay} />
                    <div className={Styles.heroInner}>
                        <p className={Styles.heroEyebrow}>Resources</p>
                        <p className={Styles.heroKicker}>Iron Lady</p>
                        <h1 className={Styles.heroTitle}>Video Resources</h1>
                        <p className={Styles.heroLead}>
                            Talks, testimonials, and C-suite conversations — curated for leaders who want to go further.
                        </p>
                    </div>
                </section>
            ) : (
                <header className={Styles.speaksMasthead}>
                    <nav className={Styles.speaksBreadcrumb} aria-label="Breadcrumb">
                        <Link to="/" className={Styles.speaksBreadcrumbLink}>
                            Home
                        </Link>
                        <span className={Styles.speaksBreadcrumbSep} aria-hidden>
                            ›
                        </span>
                        <Link to="/resources/video-resources" className={Styles.speaksBreadcrumbLink}>
                            Resources
                        </Link>
                        <span className={Styles.speaksBreadcrumbSep} aria-hidden>
                            ›
                        </span>
                        {speaksDetailMode ? (
                            <>
                                <Link
                                    to="/resources/video-resources?speaksOnly=1"
                                    className={Styles.speaksBreadcrumbLink}
                                >
                                    Iron Lady Speaks
                                </Link>
                                <span className={Styles.speaksBreadcrumbSep} aria-hidden>
                                    ›
                                </span>
                                <span className={Styles.speaksBreadcrumbCurrent}>
                                    {currentVideo?.title && currentVideo.title.length > 52
                                        ? `${currentVideo.title.slice(0, 52)}…`
                                        : currentVideo?.title || 'Talk'}
                                </span>
                            </>
                        ) : (
                            <span className={Styles.speaksBreadcrumbCurrent}>Iron Lady Speaks</span>
                        )}
                    </nav>
                </header>
            )}

            <section
                className={
                    speaksOnly
                        ? `${Styles.speaksCatalogSection} ${Styles.speaksCatalogCover}`
                        : Styles.mainSection
                }
                aria-label={speaksOnly ? 'Iron Lady Speaks podcast library' : 'Video library'}
            >
                <div className={Styles.mainInner}>
                    {showPodcastCmsNote && (
                        <div className={Styles.podcastCmsNote} role="status">
                            <p className={Styles.podcastCmsNoteText}>
                                {podcastCmsInfo?.bundleStatus === 404 && (
                                    <>
                                        The WordPress video list API is missing (
                                        <code className={Styles.cmsCode}>/wp-json/il/v1/video-podcasts</code>
                                        {` returns 404). Upload and activate the plugin in `}
                                        <code className={Styles.cmsCode}>
                                            docs/wordpress-plugin-iron-lady-video-podcasts
                                        </code>
                                        {` on the CMS, then use “Clear REST cache” in the admin bar. `}
                                    </>
                                )}
                                {podcastCmsInfo?.bundleStatus === 0 && (
                                    <>
                                        The site could not reach the podcast list API. Check your connection or try
                                        again.{' '}
                                    </>
                                )}
                                {podcastCmsInfo &&
                                    podcastCmsInfo.v2PostCount > 0 &&
                                    podcastCmsInfo.v2MappedCount < podcastCmsInfo.v2PostCount && (
                                        <>
                                            {podcastCmsInfo.v2PostCount - podcastCmsInfo.v2MappedCount} published
                                            talk(s) in the CMS are missing a YouTube link the app can read—add a
                                            video URL to each post (Pods field or post meta) and republish.{' '}
                                        </>
                                    )}
                            </p>
                            <button
                                type="button"
                                className={Styles.cmsNoteDismiss}
                                onClick={() => {
                                    setDismissCmsNote(true);
                                    if (typeof sessionStorage !== 'undefined') {
                                        sessionStorage.setItem(CMS_NOTE_DISMISS, '1');
                                    }
                                }}
                            >
                                Dismiss
                            </button>
                        </div>
                    )}
                    {!speaksOnly && (
                        <div className={Styles.tabsRow}>
                            {RESOURCE_PAGE_TAB_IDS.map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    className={`${Styles.tab} ${activeTab === tab ? Styles.tabActive : ''}`}
                                    onClick={() => handleTabClick(tab)}
                                >
                                    {tabNames[tab] || tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            ))}
                        </div>
                    )}

                    {speaksOnly ? (
                        speaksListMode ? (
                            <div className={Styles.speaksIndexWrap}>
                                <div className={Styles.speaksArticleToolbar}>
                                    <label className={Styles.speaksArticleSearchLabel} htmlFor="il-speaks-search">
                                        Search talks
                                    </label>
                                    <input
                                        id="il-speaks-search"
                                        type="search"
                                        className={Styles.speaksArticleSearchInput}
                                        placeholder="Type to filter talks…"
                                        value={speaksQuery}
                                        onChange={(e) => {
                                            setSpeaksQuery(e.target.value);
                                            setSpeaksPageNum(1);
                                        }}
                                        autoComplete="off"
                                    />
                                </div>

                                <section
                                    className={Styles.speaksIndexSection}
                                    aria-labelledby="il-speaks-index-heading"
                                >
                                    <h2 id="il-speaks-index-heading" className={Styles.speaksGridHeading}>
                                        Talks
                                    </h2>

                                    {speaksPagedVideos.length === 0 ? (
                                        <p className={Styles.speaksEmptyState} role="status">
                                            No talks match your search.
                                        </p>
                                    ) : (
                                        <div className={Styles.speaksGrid} role="list">
                                            {speaksPagedVideos.map((video) => {
                                                const yid = embedUrlToYoutubeId(video.url);
                                                const cardThumbCms = normalizeEpisodeImageUrl(video.imageUrl);
                                                const cardThumbYt = yid
                                                    ? `https://img.youtube.com/vi/${yid}/hqdefault.jpg`
                                                    : '';
                                                const dateLine = formatEpisodeDate(video.publishedAt);
                                                return (
                                                    <Link
                                                        key={video.id}
                                                        role="listitem"
                                                        to={speaksEpisodeUrl(video.id)}
                                                        className={`${Styles.speaksCard} ${Styles.speaksCardLink}`}
                                                    >
                                                        <div className={Styles.speaksCardThumb}>
                                                            <img
                                                                src={cardThumbCms || cardThumbYt}
                                                                alt={video.title}
                                                                onError={(e) => {
                                                                    e.currentTarget.onerror = null;
                                                                    if (
                                                                        cardThumbYt &&
                                                                        e.currentTarget.src !== cardThumbYt
                                                                    ) {
                                                                        e.currentTarget.src = cardThumbYt;
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        {dateLine && video.publishedAt ? (
                                                            <time
                                                                className={Styles.speaksCardDate}
                                                                dateTime={video.publishedAt}
                                                            >
                                                                {dateLine}
                                                            </time>
                                                        ) : null}
                                                        <h3 className={Styles.speaksCardTitle}>{video.title}</h3>
                                                        <span className={Styles.speaksCardTag}>Iron Lady Speaks</span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {speaksTotalPages > 1 && speaksPagedVideos.length > 0 && (
                                        <nav className={Styles.speaksPagination} aria-label="Pagination">
                                            <button
                                                type="button"
                                                className={Styles.speaksPageBtn}
                                                disabled={speaksPageNum <= 1}
                                                onClick={() => setSpeaksPageNum((p) => Math.max(1, p - 1))}
                                            >
                                                Previous
                                            </button>
                                            {Array.from({ length: speaksTotalPages }, (_, i) => i + 1).map((p) => (
                                                <button
                                                    key={p}
                                                    type="button"
                                                    className={
                                                        p === speaksPageNum
                                                            ? `${Styles.speaksPageBtn} ${Styles.speaksPageBtnActive}`
                                                            : Styles.speaksPageBtn
                                                    }
                                                    onClick={() => setSpeaksPageNum(p)}
                                                    aria-current={p === speaksPageNum ? 'page' : undefined}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                className={Styles.speaksPageBtn}
                                                disabled={speaksPageNum >= speaksTotalPages}
                                                onClick={() =>
                                                    setSpeaksPageNum((p) => Math.min(speaksTotalPages, p + 1))
                                                }
                                            >
                                                Next
                                            </button>
                                        </nav>
                                    )}
                                </section>
                            </div>
                        ) : (
                            <div className={Styles.speaksArticleCover}>
                                <div className={Styles.speaksFeatureLayout}>
                                    <div className={Styles.speaksFeatureMain}>
                                        <div
                                            className={`${Styles.speaksFeaturedPanel} ${Styles.speaksFeaturedPanelCover}`}
                                        >
                                            {speaksArticleFeature}
                                        </div>
                                    </div>

                                    <aside className={Styles.speaksEpisodesSidebar} aria-label="Talk count and list">
                                        <div className={Styles.speaksSidebarStat}>
                                            <p className={Styles.speaksSidebarStatEyebrow}>Iron Lady Speaks</p>
                                            <p className={Styles.speaksSidebarStatNumber}>
                                                {speaksFilteredVideos.length}
                                            </p>
                                            <p className={Styles.speaksSidebarStatLabel}>
                                                Talk
                                                {speaksFilteredVideos.length !== 1 ? 's' : ''}
                                                {speaksQuery.trim() ? ' matching filter' : ' in library'}
                                            </p>
                                            {speaksQuery.trim() && speaksFilteredVideos.length !== videos.length ? (
                                                <p className={Styles.speaksSidebarStatHint}>
                                                    {videos.length} total podcast post
                                                    {videos.length !== 1 ? 's' : ''}
                                                </p>
                                            ) : null}
                                        </div>

                                        <h3 className={Styles.speaksSidebarHeading}>Talks</h3>

                                        {speaksFilteredVideos.length === 0 ? (
                                            <p className={Styles.speaksSidebarEmpty} role="status">
                                                No talks match your search.
                                            </p>
                                        ) : (
                                            <ul className={Styles.speaksSidebarList}>
                                                {speaksFilteredVideos.map((video) => {
                                                    const yid = embedUrlToYoutubeId(video.url);
                                                    const cardThumbCms = normalizeEpisodeImageUrl(video.imageUrl);
                                                    const cardThumbYt = yid
                                                        ? `https://img.youtube.com/vi/${yid}/hqdefault.jpg`
                                                        : '';
                                                    const selected = currentVideo?.id === video.id;
                                                    const dateLine = formatEpisodeDate(video.publishedAt);
                                                    return (
                                                        <li key={video.id}>
                                                            <Link
                                                                className={`${Styles.speaksSidebarRow} ${Styles.speaksSidebarRowLink} ${selected ? Styles.speaksSidebarRowSelected : ''}`}
                                                                to={speaksEpisodeUrl(video.id)}
                                                                onClick={() => {
                                                                    requestAnimationFrame(() => {
                                                                        document
                                                                            .getElementById('il-speaks-player')
                                                                            ?.scrollIntoView({
                                                                                behavior: 'smooth',
                                                                                block: 'start',
                                                                            });
                                                                    });
                                                                }}
                                                            >
                                                                <div className={Styles.speaksSidebarThumb}>
                                                                    <img
                                                                        src={cardThumbCms || cardThumbYt}
                                                                        alt=""
                                                                        onError={(e) => {
                                                                            e.currentTarget.onerror = null;
                                                                            if (
                                                                                cardThumbYt &&
                                                                                e.currentTarget.src !== cardThumbYt
                                                                            ) {
                                                                                e.currentTarget.src = cardThumbYt;
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className={Styles.speaksSidebarRowText}>
                                                                    <p className={Styles.speaksSidebarRowTitle}>
                                                                        {video.title}
                                                                    </p>
                                                                    {dateLine && video.publishedAt ? (
                                                                        <time
                                                                            className={Styles.speaksSidebarRowDate}
                                                                            dateTime={video.publishedAt}
                                                                        >
                                                                            {dateLine}
                                                                        </time>
                                                                    ) : null}
                                                                </div>
                                                            </Link>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        )}
                                    </aside>
                                </div>
                            </div>
                        )
                    ) : (
                        <div
                            className={`${Styles.layout} ${
                                videos.length <= 1 ? Styles.layoutSingleVideo : ''
                            }`}
                        >
                            <div className={Styles.playerColumn}>{classicPlayerInner}</div>

                            {videos.length > 1 && (
                                <aside
                                    className={Styles.sidebar}
                                    aria-label={`More videos: ${labelBase.trim()}`}
                                >
                                    <h3 className={Styles.sidebarHeading}>
                                        {labelBase.trim()}
                                        <span className={Styles.sidebarHeadingSuffix}>More in this list</span>
                                    </h3>
                                    <div className={Styles.cardList}>
                                        {videos.slice(1, visibleVideos + 1).map((video) => {
                                            const yid = embedUrlToYoutubeId(video.url);
                                            return (
                                                <div
                                                    key={video.id}
                                                    className={Styles.relatedCard}
                                                    onClick={() => handleVideoClick(video)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            handleVideoClick(video);
                                                        }
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <div className={Styles.thumbWrap}>
                                                        <img
                                                            src={`https://img.youtube.com/vi/${yid}/mqdefault.jpg`}
                                                            alt={video.title}
                                                        />
                                                        <div className={Styles.playIcon} aria-hidden />
                                                    </div>
                                                    <p className={Styles.cardTitle}>
                                                        {video.title.length > 72
                                                            ? `${video.title.substring(0, 72)}…`
                                                            : video.title}
                                                    </p>
                                                    {activeTab === 'podcast' && video.summary && (
                                                        <p className={Styles.cardSummary}>
                                                            {truncateSummary(video.summary, 100)}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {visibleVideos + 1 < videos.length && (
                                        <button
                                            type="button"
                                            className={Styles.loadMore}
                                            onClick={handleLoadMore}
                                        >
                                            Load more
                                        </button>
                                    )}
                                </aside>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default VideoResources;
